// FilterBar (search + filter chips + saved views + sort) and useListState, which keeps a list
// page's search / filters / sort / view in the URL so every filtered view is shareable.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useSearchParams } from 'react-router'
import { ArrowDownUp, Check, ListFilter, Plus, X, Bookmark, Pencil, Trash2 } from 'lucide-react'
import { cn } from './cn'
import { Button } from './button'
import { SearchInput, Input, NumberInput, MoneyInput } from './input'
import { Checkbox, RadioGroup } from './checkbox'
import { DropdownMenu, MenuItem, MenuLabel, MenuRadioGroup, MenuSeparator, Popover } from './menu'
import { DatePicker } from './date-picker'
import { DATE_PRESETS, resolvePreset, type DateRange } from './date-range'
import { Dialog } from './dialog'
import { Field } from './field'
import type { SortState } from './data-table'
import { useLocalStorage } from '@/lib/hooks'
import { getStoreId } from '@/lib/api'
import { number as fmtNumber } from '@/lib/format'

export { encodeFilter, decodeFilter, filterQueryParams, filterChipLabel, isEmptyValue, isDayKey, type FilterDef, type FilterValues, type NumberRange } from './filter-codec'
import { encodeFilter, decodeFilter, filterQueryParams, filterChipLabel, isEmptyValue, type FilterDef, type FilterValues, type NumberRange } from './filter-codec'

// ─── useListState ───────────────────────────────────────────────────────────────────────

export interface SavedView {
  id: string
  label: string
  filters: FilterValues
  search?: string
  sort?: SortState | null
  /** Built-in views can't be renamed or deleted. */
  builtIn?: boolean
}

export interface ListStateOptions {
  filters?: FilterDef[]
  defaultSort?: SortState | null
  /** Built-in views (tabs). The first one is the default ("All"). */
  views?: SavedView[]
  /** Enables merchant-saved views stored per store in localStorage under this key. */
  savedViewsKey?: string
  /** Search debounce in ms (default 300). */
  debounce?: number
  /** Also send `view=<id>` in `query` while a built-in view (other than the first) is active — for
   *  APIs with server-side views such as GET /orders?view=unpaid. Merchant-saved views never are. */
  viewParam?: boolean
}

const encodeSort = (s: SortState | null | undefined) => (s ? `${s.desc ? '-' : ''}${s.id}` : undefined)
const decodeSort = (v: string | null): SortState | null => (v && /^-?[\w.]+$/.test(v) ? { id: v.replace(/^-/, ''), desc: v.startsWith('-') } : null)
const sameSort = (a?: SortState | null, b?: SortState | null) => (a?.id ?? null) === (b?.id ?? null) && (a?.desc ?? false) === (b?.desc ?? false)

/**
 * URL-synced list state for index pages. Everything (search, filters, sort, view) lives in the
 * query string, so a filtered view can be bookmarked or shared.
 * ```
 * const list = useListState({ filters: FILTERS, defaultSort: { id: 'createdAt', desc: true }, views: VIEWS, savedViewsKey: 'orders' })
 * const q = useInfiniteQuery({ queryKey: ['orders', list.query], queryFn: ({ pageParam }) => api.get(`/api/admin/orders${qs({ ...list.query, cursor: pageParam })}`), … })
 * <FilterBar {...list.filterBarProps} />
 * <DataTable sort={list.sort} onSortChange={list.setSort} … />
 * ```
 * `list.query` = { q?, sort?, <filterId>: encoded, from?/to? (date ranges, resolved store-local days), view? (with
 * `viewParam`) } — filter ids double as API query params. Pass `selectionScope={list.query}` … DataTable resets its
 * selection when the query changes (it also does this on its own from the URL).
 */
export function useListState(opts: ListStateOptions = {}) {
  const defs = opts.filters ?? []
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  // Writes build on the LIVE query string: React Router's setSearchParams(fn) hands the updater the
  // params of the render that created it, so a debounced search write would otherwise wipe a sort or
  // filter change made during the debounce. `latest` also lets back-to-back writes compose.
  const latest = useRef({ pathname: location.pathname, search: location.search })
  latest.current = { pathname: location.pathname, search: location.search }
  const setParamsRef = useRef(setParams)
  setParamsRef.current = setParams
  const builtIn = opts.views ?? []
  const storeKey = opts.savedViewsKey ? `views:${getStoreId() ?? 'none'}:${opts.savedViewsKey}` : null
  const isViews = (v: unknown): v is SavedView[] => Array.isArray(v) && v.every(x => x && typeof x === 'object' && typeof (x as SavedView).id === 'string' && typeof (x as SavedView).label === 'string')
  const [custom, setCustom] = useLocalStorage<SavedView[]>(storeKey, [], isViews)
  const views = useMemo(() => [...builtIn.map(v => ({ ...v, builtIn: true })), ...(opts.savedViewsKey ? custom : [])], [builtIn, custom, opts.savedViewsKey])

  // Read state from the URL.
  const urlSearch = params.get('q') ?? ''
  const sort = decodeSort(params.get('sort')) ?? opts.defaultSort ?? null
  const filters = useMemo(() => {
    const out: FilterValues = {}
    for (const d of defs) { const v = decodeFilter(d, params.get(d.id)); if (v !== undefined) out[d.id] = v }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, defs.map(d => d.id).join('|')])
  const viewId = params.get('view') ?? views[0]?.id ?? null
  const activeView = views.find(v => v.id === viewId) ?? null

  // Local, instantly-updating search box; URL follows after a debounce.
  const [search, setSearchLocal] = useState(urlSearch)
  const lastPushed = useRef(urlSearch)
  useEffect(() => { if (urlSearch !== lastPushed.current) { setSearchLocal(urlSearch); lastPushed.current = urlSearch } }, [urlSearch])

  const write = useCallback((mut: (p: URLSearchParams) => void, replace = true) => {
    const live = typeof window !== 'undefined' && window.location.pathname === latest.current.pathname ? window.location.search : latest.current.search
    const p = new URLSearchParams(live)
    mut(p); p.delete('cursor')
    const next = p.toString()
    latest.current = { ...latest.current, search: next ? `?${next}` : '' }
    setParamsRef.current(p, { replace })
  }, [])

  useEffect(() => {
    if (search === urlSearch) return
    const t = setTimeout(() => { lastPushed.current = search; write(p => { if (search) p.set('q', search); else p.delete('q') }) }, opts.debounce ?? 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const setFilter = useCallback((id: string, value: unknown) => {
    const def = defs.find(d => d.id === id)
    if (!def) return
    write(p => { const enc = encodeFilter(def, value); if (enc === undefined) p.delete(id); else p.set(id, enc) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [write, defs.map(d => d.id).join('|')])
  const setFilters = useCallback((values: FilterValues) => {
    write(p => { for (const d of defs) { const enc = encodeFilter(d, values[d.id]); if (enc === undefined) p.delete(d.id); else p.set(d.id, enc) } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [write, defs.map(d => d.id).join('|')])
  const setSort = useCallback((s: SortState | null) => {
    write(p => { const enc = sameSort(s, opts.defaultSort) ? undefined : encodeSort(s); if (enc) p.set('sort', enc); else p.delete('sort') })
  }, [write, opts.defaultSort])
  const setSearch = useCallback((s: string) => setSearchLocal(s), [])
  const clearFilters = useCallback(() => { setSearchLocal(''); lastPushed.current = ''; write(p => { p.delete('q'); for (const d of defs) p.delete(d.id) }) }, [write]) // eslint-disable-line react-hooks/exhaustive-deps

  const applyView = useCallback((id: string) => {
    const v = views.find(x => x.id === id)
    if (!v) return
    setSearchLocal(v.search ?? ''); lastPushed.current = v.search ?? ''
    write(p => {
      for (const d of defs) p.delete(d.id)
      p.delete('q'); p.delete('sort')
      for (const d of defs) { const enc = encodeFilter(d, v.filters[d.id]); if (enc !== undefined) p.set(d.id, enc) }
      if (v.search) p.set('q', v.search)
      const enc = v.sort && !sameSort(v.sort, opts.defaultSort) ? encodeSort(v.sort) : undefined
      if (enc) p.set('sort', enc)
      if (id === views[0]?.id) p.delete('view'); else p.set('view', id)
    }, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views, write, opts.defaultSort])

  const current: SavedView = { id: '__current', label: '', filters, search: urlSearch, sort }
  const modified = !!activeView && !viewMatches(activeView, current, defs, opts.defaultSort ?? null)

  const saveView = useCallback((label: string) => {
    const id = `v_${Date.now().toString(36)}`
    setCustom(list => [...list, { id, label: label.trim().slice(0, 40) || 'Untitled view', filters, search: urlSearch || undefined, sort }])
    write(p => p.set('view', id), false)
    return id
  }, [filters, urlSearch, sort, setCustom, write])
  const updateView = useCallback((id: string) => setCustom(list => list.map(v => (v.id === id ? { ...v, filters, search: urlSearch || undefined, sort } : v))), [filters, urlSearch, sort, setCustom])
  const renameView = useCallback((id: string, label: string) => setCustom(list => list.map(v => (v.id === id ? { ...v, label: label.trim().slice(0, 40) || v.label } : v))), [setCustom])
  const deleteView = useCallback((id: string) => { setCustom(list => list.filter(v => v.id !== id)); if (viewId === id) applyView(views[0]?.id ?? '') }, [setCustom, viewId, applyView, views])

  const sortKey = encodeSort(sort) ?? ''
  const serverView = opts.viewParam && activeView && activeView.builtIn && activeView.id !== views[0]?.id ? activeView.id : null
  const query = useMemo(() => {
    const q: Record<string, string> = {}
    if (urlSearch.trim()) q.q = urlSearch.trim()
    if (sortKey) q.sort = sortKey
    for (const d of defs) Object.assign(q, filterQueryParams(d, filters[d.id]))
    if (serverView) q.view = serverView
    return q
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch, sortKey, filters, serverView])

  const activeFilterCount = Object.keys(filters).length + (urlSearch ? 1 : 0)

  return {
    search, setSearch, debouncedSearch: urlSearch,
    filters, setFilter, setFilters, clearFilters, activeFilterCount,
    sort, setSort,
    views, view: activeView?.id ?? null, setView: applyView, modified,
    saveView, updateView, renameView, deleteView,
    query,
    filterBarProps: {
      search, onSearchChange: setSearch,
      filters: defs, values: filters, onFilterChange: setFilter, onClearAll: clearFilters,
      views: views.length > 1 || opts.savedViewsKey ? views : undefined, activeView: activeView?.id, onViewChange: applyView,
      modified, onSaveView: opts.savedViewsKey ? saveView : undefined, onUpdateView: opts.savedViewsKey ? updateView : undefined,
      onRenameView: opts.savedViewsKey ? renameView : undefined, onDeleteView: opts.savedViewsKey ? deleteView : undefined,
    } satisfies Partial<FilterBarProps>,
  }
}

function viewMatches(v: SavedView, cur: SavedView, defs: FilterDef[], defSort: SortState | null): boolean {
  if ((v.search ?? '') !== (cur.search ?? '')) return false
  if (!sameSort(v.sort ?? defSort, cur.sort ?? defSort)) return false
  for (const d of defs) if (encodeFilter(d, v.filters[d.id]) !== encodeFilter(d, cur.filters[d.id])) return false
  return true
}

// ─── FilterBar ──────────────────────────────────────────────────────────────────────────

export interface FilterBarProps {
  search: string
  onSearchChange: (v: string) => void
  searchPlaceholder?: string
  filters?: FilterDef[]
  values?: FilterValues
  onFilterChange?: (id: string, value: unknown) => void
  onClearAll?: () => void
  views?: SavedView[]
  activeView?: string
  onViewChange?: (id: string) => void
  /** Current state differs from the active view (offer Save / Update). */
  modified?: boolean
  onSaveView?: (label: string) => void
  onUpdateView?: (id: string) => void
  onRenameView?: (id: string, label: string) => void
  onDeleteView?: (id: string) => void
  /** Sort menu (useful when the list isn't a table, and on mobile). */
  sortOptions?: Array<{ id: string; label: string; descFirst?: boolean }>
  sort?: SortState | null
  onSortChange?: (s: SortState | null) => void
  /** Right side of the views row (e.g. export button). */
  actions?: ReactNode
  className?: string
}

/**
 * Toolbar for list pages; usually fed by useListState: `<FilterBar {...list.filterBarProps} searchPlaceholder="Search orders" />`.
 * Put it at the top of the same Card as a flush DataTable.
 */
export function FilterBar({ search, onSearchChange, searchPlaceholder = 'Search', filters = [], values = {}, onFilterChange, onClearAll, views, activeView, onViewChange, modified, onSaveView, onUpdateView, onRenameView, onDeleteView, sortOptions, sort, onSortChange, actions, className }: FilterBarProps) {
  const [editing, setEditing] = useState<string | null>(null)
  const [saveOpen, setSaveOpen] = useState(false)
  const [renameId, setRenameId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const active = filters.filter(f => values[f.id] !== undefined && !isEmptyValue(values[f.id]))
  const inactive = filters.filter(f => !active.includes(f))
  const pendingChip = editing && !active.some(a => a.id === editing) ? filters.find(f => f.id === editing) : null
  const current = views?.find(v => v.id === activeView)
  const anything = active.length > 0 || !!search

  return (
    <div className={cn('flex flex-col', className)}>
      {views && views.length > 0 && (
        <div className="flex items-center gap-2 border-b border-border px-2 pt-2">
          <div role="tablist" aria-label="Saved views" className="-mb-px flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {views.map(v => {
              const sel = v.id === activeView
              return (
                <div key={v.id} className="group/tab relative flex shrink-0 items-center">
                  <button type="button" role="tab" aria-selected={sel} onClick={() => onViewChange?.(v.id)}
                    className={cn('flex h-9 items-center gap-2 border-b-2 px-3 text-base font-medium transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus',
                      sel ? 'border-text text-text' : 'border-transparent text-text-muted hover:text-text')}>
                    {v.label}
                    {sel && modified && <span className="size-1.5 rounded-full bg-primary" aria-label="(modified)" />}
                  </button>
                  {sel && !v.builtIn && (onRenameView || onDeleteView) && (
                    <DropdownMenu trigger={<button type="button" aria-label={`${v.label} view options`} className="-ml-2 mr-1 grid size-5 place-items-center rounded text-text-subtle hover:bg-surface-active hover:text-text"><Pencil className="size-3" /></button>}>
                      {onRenameView && <MenuItem icon={<Pencil />} onSelect={() => { setRenameId(v.id); setName(v.label) }}>Rename view</MenuItem>}
                      {onDeleteView && <MenuItem destructive icon={<Trash2 />} onSelect={() => onDeleteView(v.id)}>Delete view</MenuItem>}
                    </DropdownMenu>
                  )}
                </div>
              )
            })}
          </div>
          <div className="flex shrink-0 items-center gap-2 pb-1">
            {modified && current && !current.builtIn && onUpdateView && <Button size="sm" variant="ghost" onClick={() => onUpdateView(current.id)}>Update view</Button>}
            {modified && onSaveView && <Button size="sm" variant="ghost" icon={<Bookmark />} onClick={() => { setName(''); setSaveOpen(true) }}>Save as view</Button>}
            {actions}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 p-2">
        <SearchInput value={search} onChange={onSearchChange} placeholder={searchPlaceholder} wrapperClassName="min-w-[200px] flex-1 sm:max-w-sm" />
        {active.map(f => (
          <FilterChip key={f.id} def={f} value={values[f.id]} open={editing === f.id} onOpenChange={o => setEditing(o ? f.id : null)}
            onChange={v => onFilterChange?.(f.id, v)} onRemove={() => onFilterChange?.(f.id, undefined)} />
        ))}
        {pendingChip && (
          <FilterChip def={pendingChip} value={undefined} open onOpenChange={o => { if (!o) setEditing(null) }}
            onChange={v => onFilterChange?.(pendingChip.id, v)} onRemove={() => setEditing(null)} />
        )}
        {inactive.length > 0 && !pendingChip && (
          <DropdownMenu align="start" trigger={<Button size="md" variant="ghost" icon={active.length ? <Plus /> : <ListFilter />} className="border border-dashed border-border-strong">{active.length ? 'Add filter' : 'Filter'}</Button>}>
            <MenuLabel>Filter by</MenuLabel>
            {inactive.map(f => <MenuItem key={f.id} onSelect={() => setTimeout(() => setEditing(f.id), 0)}>{f.label}</MenuItem>)}
          </DropdownMenu>
        )}
        {anything && onClearAll && <Button size="md" variant="ghost" onClick={onClearAll}>Clear all</Button>}
        {sortOptions && onSortChange && (
          <div className="ml-auto">
            <DropdownMenu trigger={<Button icon={<ArrowDownUp />} tooltip="Sort" aria-label="Sort" />}>
              <MenuLabel>Sort by</MenuLabel>
              <MenuRadioGroup value={sort?.id ?? ''} onValueChange={id => onSortChange({ id, desc: sortOptions.find(o => o.id === id)?.descFirst ?? false })}
                options={sortOptions.map(o => ({ value: o.id, label: o.label }))} />
              <MenuSeparator />
              <MenuRadioGroup value={sort?.desc ? 'desc' : 'asc'} onValueChange={d => sort && onSortChange({ id: sort.id, desc: d === 'desc' })}
                options={[{ value: 'asc', label: 'Ascending' }, { value: 'desc', label: 'Descending' }]} />
            </DropdownMenu>
          </div>
        )}
      </div>
      <Dialog open={saveOpen || !!renameId} onOpenChange={o => { if (!o) { setSaveOpen(false); setRenameId(null) } }} size="sm"
        title={renameId ? 'Rename view' : 'Save as a new view'}
        description={renameId ? undefined : 'Saves the current search, filters and sort as a tab you can come back to.'}
        footer={<>
          <Button onClick={() => { setSaveOpen(false); setRenameId(null) }}>Cancel</Button>
          <Button variant="primary" disabled={!name.trim()} onClick={() => {
            if (renameId) onRenameView?.(renameId, name); else onSaveView?.(name)
            setSaveOpen(false); setRenameId(null)
          }}>{renameId ? 'Rename' : 'Save view'}</Button>
        </>}>
        <form onSubmit={e => { e.preventDefault(); if (!name.trim()) return; if (renameId) onRenameView?.(renameId, name); else onSaveView?.(name); setSaveOpen(false); setRenameId(null) }}>
          <Field label="View name"><Input autoFocus value={name} maxLength={40} onChange={e => setName(e.target.value)} placeholder="e.g. Unpaid COD orders" /></Field>
        </form>
      </Dialog>
    </div>
  )
}

function FilterChip({ def, value, open, onOpenChange, onChange, onRemove }: { def: FilterDef; value: unknown; open: boolean; onOpenChange: (o: boolean) => void; onChange: (v: unknown) => void; onRemove: () => void }) {
  const has = value !== undefined && !isEmptyValue(value)
  return (
    <Popover open={open} onOpenChange={onOpenChange} className="w-72 p-0"
      trigger={
        <span className={cn('inline-flex h-8 items-center overflow-hidden rounded-control border text-base shadow-control',
          has ? 'border-primary/30 bg-primary-soft/60 dark:border-primary/40' : 'border-dashed border-border-strong bg-surface')}>
          <button type="button" className="flex h-full min-w-0 items-center gap-1 pr-2 pl-3 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]">
            <span className="text-text-muted">{def.label}{has && ':'}</span>
            {has && <span className="max-w-[180px] truncate font-medium text-text">{filterChipLabel(def, value)}</span>}
          </button>
          <button type="button" aria-label={`Remove ${def.label} filter`} onClick={e => { e.stopPropagation(); onRemove() }}
            className="grid h-full w-7 place-items-center border-l border-inherit text-text-subtle hover:bg-black/[0.05] hover:text-text dark:hover:bg-white/[0.06]"><X className="size-3.5" /></button>
        </span>
      }>
      <FilterEditor def={def} value={value} onChange={onChange} close={() => onOpenChange(false)} />
    </Popover>
  )
}

function FilterEditor({ def, value, onChange, close }: { def: FilterDef; value: unknown; onChange: (v: unknown) => void; close: () => void }) {
  const [q, setQ] = useState('')
  const [draftText, setDraftText] = useState(typeof value === 'string' ? value : '')
  const [range, setRange] = useState<NumberRange>((value as NumberRange) ?? { min: null, max: null })
  const header = <div className="border-b border-border px-3 py-2 text-sm font-medium text-text-muted">{def.label}</div>
  switch (def.type) {
    case 'multi': {
      const sel = new Set(Array.isArray(value) ? (value as string[]) : [])
      const opts = def.options.filter(o => !q || o.label.toLowerCase().includes(q.toLowerCase()))
      return (
        <div className="flex flex-col">
          {header}
          {def.options.length > 8 && <div className="border-b border-border p-2"><SearchInput value={q} onChange={setQ} pageSearch={false} size="sm" autoFocus /></div>}
          <div className="flex max-h-64 flex-col gap-1 overflow-y-auto p-1">
            {opts.map(o => (
              <label key={o.value} className="flex h-8 cursor-pointer items-center gap-2 rounded-[6px] px-2 text-base hover:bg-surface-active">
                <Checkbox checked={sel.has(o.value)} onCheckedChange={c => { const n = new Set(sel); if (c) n.add(o.value); else n.delete(o.value); onChange(def.options.map(x => x.value).filter(x => n.has(x))) }} aria-label={o.label} />
                <span className="truncate">{o.label}</span>
              </label>
            ))}
            {!opts.length && <p className="px-2 py-3 text-sm text-text-muted">No options match.</p>}
          </div>
          {sel.size > 0 && <div className="flex justify-end border-t border-border p-2"><Button size="sm" variant="ghost" onClick={() => onChange(undefined)}>Clear</Button></div>}
        </div>
      )
    }
    case 'select':
      return (
        <div className="flex flex-col">{header}
          <div className="flex max-h-64 flex-col gap-1 overflow-y-auto p-1">
            {def.options.map(o => (
              <button key={o.value} type="button" onClick={() => { onChange(o.value); close() }}
                className="flex h-8 items-center justify-between gap-2 rounded-[6px] px-2 text-left text-base hover:bg-surface-active">
                {o.label}{value === o.value && <Check className="size-4 text-primary-strong" />}
              </button>
            ))}
          </div>
        </div>
      )
    case 'boolean':
      return (
        <div className="flex flex-col">{header}
          <div className="p-3">
            <RadioGroup value={value === true ? 'true' : value === false ? 'false' : null} onValueChange={v => onChange(v === 'true')}
              options={[{ value: 'true', label: def.trueLabel ?? 'Yes' }, { value: 'false', label: def.falseLabel ?? 'No' }]} />
          </div>
        </div>
      )
    case 'text':
      return (
        <form className="flex flex-col" onSubmit={e => { e.preventDefault(); onChange(draftText.trim() || undefined); close() }}>{header}
          <div className="flex flex-col gap-2 p-3">
            <Input autoFocus value={draftText} onChange={e => setDraftText(e.target.value)} placeholder={def.placeholder ?? 'Contains…'} maxLength={200} />
            <div className="flex justify-end"><Button size="sm" variant="primary" type="submit">Apply</Button></div>
          </div>
        </form>
      )
    case 'number-range':
      return (
        <form className="flex flex-col" onSubmit={e => { e.preventDefault(); onChange(range.min == null && range.max == null ? undefined : range); close() }}>{header}
          <div className="grid grid-cols-2 gap-2 p-3">
            <Field label="Min">{def.money ? <MoneyInput value={range.min ?? null} onChange={v => setRange(r => ({ ...r, min: v }))} /> : <NumberInput value={range.min ?? null} onChange={v => setRange(r => ({ ...r, min: v }))} stepper={false} />}</Field>
            <Field label="Max">{def.money ? <MoneyInput value={range.max ?? null} onChange={v => setRange(r => ({ ...r, max: v }))} /> : <NumberInput value={range.max ?? null} onChange={v => setRange(r => ({ ...r, max: v }))} stepper={false} />}</Field>
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-3 py-2"><Button size="sm" variant="primary" type="submit">Apply</Button></div>
        </form>
      )
    case 'date-range': {
      const r = value as DateRange | undefined
      return (
        <div className="flex flex-col">{header}
          <div className="flex flex-col gap-1 p-1">
            {DATE_PRESETS.slice(0, 7).map(p => (
              <button key={p.id} type="button" onClick={() => { onChange(resolvePreset(p.id)); close() }}
                className="flex h-8 items-center justify-between rounded-[6px] px-2 text-left text-base hover:bg-surface-active">
                {p.label}{r?.preset === p.id && <Check className="size-4 text-primary-strong" />}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 border-t border-border p-3">
            <Field label="From"><DatePicker value={r?.from ?? null} clearable={false} onChange={d => d && onChange({ from: d, to: r?.to && r.to >= d ? r.to : d, preset: 'custom' })} /></Field>
            <Field label="To"><DatePicker value={r?.to ?? null} clearable={false} min={r?.from} onChange={d => d && onChange({ from: r?.from && r.from <= d ? r.from : d, to: d, preset: 'custom' })} /></Field>
          </div>
        </div>
      )
    }
  }
}

// ─── Pagination ─────────────────────────────────────────────────────────────────────────

export interface LoadMoreProps {
  hasMore: boolean
  loading?: boolean
  onLoadMore: () => void
  /** Rows currently shown. */
  shown?: number
  /** Total matching rows if the API returns it. */
  total?: number
  itemName?: { singular: string; plural: string }
  /** Load the next page automatically when the footer scrolls into view. */
  auto?: boolean
  className?: string
}

/** Cursor pagination footer: "Showing 50 of 1,234 orders · Load more". Pairs with useInfiniteQuery. */
export function LoadMore({ hasMore, loading, onLoadMore, shown, total, itemName = { singular: 'item', plural: 'items' }, auto, className }: LoadMoreProps) {
  const ref = useRef<HTMLDivElement>(null)
  const cb = useRef(onLoadMore)
  cb.current = onLoadMore
  useEffect(() => {
    if (!auto || !hasMore || loading || !ref.current) return
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) cb.current() }, { rootMargin: '400px' })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [auto, hasMore, loading])
  const noun = (n: number) => (n === 1 ? itemName.singular : itemName.plural)
  if (!hasMore && shown === undefined) return null
  return (
    <div ref={ref} className={cn('flex items-center justify-between gap-3 px-4 py-3 text-sm text-text-muted', className)}>
      <span className="tabular">
        {shown !== undefined && (total !== undefined ? <>Showing {fmtNumber(shown)} of {fmtNumber(total)} {noun(total)}</> : <>{fmtNumber(shown)} {noun(shown)}</>)}
      </span>
      {hasMore && <Button size="sm" onClick={onLoadMore} loading={loading}>Load more</Button>}
    </div>
  )
}
/** Alias: the spec calls cursor pagination "Pagination". */
export const Pagination = LoadMore
